/**
 * Состояние вьюера — сигналы (@preact/signals): pull-based, без ре-рендера дерева.
 * Машина состояний одной карты (CONCEPT §4):
 * loading → overview ⇄ target ⇄ route → follow → arrived.
 */

import type { BuiltGraph, RoutePlan, Venue } from '@app/rvf'
import { computed, signal } from '@preact/signals'
import { EMPTY_BLOCKS, filterGraph, venueHazardBlocks } from './emergency/hazards'
import type { VenueMap } from './map/venue-map'
import type { NavProgress, RouteStep } from './nav'

export type AppState =
  | 'loading'
  | 'error'
  | 'overview'
  | 'target'
  | 'route'
  | 'orient'
  | 'follow'
  | 'arrived'

export const appState = signal<AppState>('loading')
export const venue = signal<Venue | null>(null)
export const graph = signal<BuiltGraph | null>(null)
/** QR-якорь из ссылки (?a=) — стартовая позиция без GPS. */
export const anchorId = signal<string | null>(null)
/** Кэш геопозиции для старта маршрута — только если разрешение УЖЕ выдано
 * (permissions: granted; промпт не показываем — он живёт на «В путь»). */
export const geoStart = signal<import('@app/geo').LonLat | null>(null)
/**
 * Последний фикс геопозиции с точностью (м) и временем (ms epoch) — для
 * решения «где человек» при тревоге (emergency/alarm-where.ts). Живёт только
 * в памяти вкладки; null — позиции нет (не спрашивали, отказ, ошибка).
 */
export const geoFix = signal<{
  readonly point: import('@app/geo').LonLat
  readonly accuracyM: number
  readonly at: number
} | null>(null)
/** Адрес и время открытия страницы (main.tsx) — свежесть QR (`?a=`) при тревоге. */
export const launchLink = signal<{ readonly search: string; readonly openedAtMs: number }>({
  search: '',
  openedAtMs: 0,
})
export const targetId = signal<string | null>(null)
/** Цель из ссылки (?to=) — применяется после готовности карты. */
export const pendingTargetId = signal<string | null>(null)
/** Пришли по прямой ссылке `?to=…`: здание цели ярко, остальные приглушены,
 *  чтобы арендатор сразу видел своё (запрос владельца 28.07). */
export const focusUnitId = signal<string | null>(null)
export const routePlan = signal<RoutePlan | null>(null)
export const navProgress = signal<NavProgress | null>(null)
/** Повороты активного маршрута — для степпера без GPS. */
export const navSteps = signal<readonly RouteStep[]>([])
export const venueMap = signal<VenueMap | null>(null)
/** Инкремент при смене языка — единая точка ре-рендера текстов. */
export const langTick = signal(0)

/** Режим показа площадки: плоская карта или изометрический «Объём». */
export type ViewMode = 'map' | 'iso'
export const viewMode = signal<ViewMode>('map')

/** Предпочтение вертикального коннектора (мультиэтажные площадки). */
export type ConnectorPref = 'escalator' | 'lift' | 'stairs'

/**
 * Доступность маршрута (запрос владельца 31.07): «без лестниц» для коляски
 * инвалидной и детской. Для РОУТЕРА оба означают одно — профиль wheelchair
 * (ни лестниц, ни эскалаторов): детскую коляску на эскалатор не пускают
 * правила большинства центров, и вести туда маршрутом нельзя. Разделены они
 * только визуально — человек выбирает свой значок.
 */
export type AccessMode = 'walk' | 'wheelchair' | 'stroller'
export const routePref = signal<ConnectorPref | null>(null)
export const accessMode = signal<AccessMode>('walk')

/** Тик «выбор подъёма ничего не изменил»: на карте один путь наверх —
 *  честно говорим это, иначе кнопка выглядит сломанной (жалоба 31.07). */
export const prefNoAltTick = signal(0)

/** «Организация проверена» (N2): ленивый бейдж ✓ в шапке площадки. */
export const venueVerified = signal(false)

/** Способ передвижения по площадке: пешком / авто / грузовой (промзоны).
 *  Показывается только там, где в сети есть транспортные дороги. */
export type TravelMode = 'foot' | 'vehicle' | 'truck'
export const travelMode = signal<TravelMode>('foot')

/** Сохранённое место машины для текущей площадки (идея №2). */
export const parkedCar = signal<import('./car').ParkedCar | null>(null)

/** Service worker готов — можно честно показывать «работает оффлайн». */
export const swReady = signal(false)

/** Вид активной опасности (учения, живая тревога площадки/города); null = не активен. */
export const emergencyHazard = signal<import('./emergency/drill').EmergencyHazard | null>(null)

/** true — аварийный режим запущен БЕЗ подписи (демо/учения): водяной знак обязателен. */
export const emergencyDrill = signal(false)

/** Горящие помещения живой тревоги — огонь-анимация на карте (live.ts). */
export const liveHazardUnits = signal<readonly string[]>([])

/** Последний ответ alert.json площадки (тревога v2, emergency/alert-types.ts); null — не получали. */
export const liveAlert = signal<import('./emergency/alert-types').LiveAlert | null>(null)

/** Городская тревога, покрывающая площадку/главную; null — нет. */
export const cityAlert = signal<import('./emergency/alert-types').CityAlert | null>(null)

/**
 * Та же городская тревога, но когда её зона площадку НЕ покрывает (человек
 * мог открыть карту вдалеке от места, где стоит). Сам по себе тревогу
 * площадки не включает — liveMode() её не читает; экран решает место
 * человека (emergency/alarm-where.ts).
 */
export const cityElsewhere = signal<import('./emergency/alert-types').CityAlert | null>(null)

/**
 * Готовый кадр для cityElsewhere (live.ts). Экран он захватывает, только если
 * человек в зоне или его место неизвестно (emergency/alarm-where.ts).
 */
export const cityElsewhereFrame = signal<import('./emergency/frame').EmergencyFrame | null>(null)

/**
 * Городская тревога в другом районе не захватила экран — строка в alarm-bar:
 * 'out' — человек уверенно вне зоны; 'unsure' — ждём позицию или человек сам
 * скрыл экран. null — строки нет (тревоги нет или экран показан).
 */
export const elsewhereQuiet = signal<'out' | 'unsure' | null>(null)

/** Выбор человека для тревоги в другом районе: развернуть экран / скрыть; null — решает место. */
export const elsewhereChoice = signal<'open' | 'hidden' | null>(null)

/**
 * Экран тревоги показывает не план площадки (человек не в здании): alarm-bar
 * не зовёт «к выходу по красной линии». 'calm' — человек далеко или вне зоны:
 * без сирены; 'danger' — рядом, в зоне или идёт в укрытие. null — план
 * площадки или тревоги нет (alarm-where.ts).
 */
export const alarmOffPlan = signal<'calm' | 'danger' | null>(null)

/** Точечные опасности площадки — действуют и без тревоги: любой маршрут их обходит. */
export const activeHazards = signal<readonly import('./emergency/alert-types').PointHazard[]>([])

/** Когда тревогу проверили успешно (ms epoch); null — ещё ни разу. */
export const alertCheckedAt = signal<number | null>(null)

/** Последняя проверка тревоги не дошла до сервера — показываем «нет связи», состояние не сбрасываем. */
export const alertOffline = signal(false)

/** Обычный маршрут идёт в обход опасности (строка «в обход … +N м»); null — кратчайший. */
export const routeDetour = signal<import('./emergency/alert-types').RouteDetour | null>(null)

/**
 * Блоки графа от опасностей и горящих помещений (emergency/hazards.ts); без
 * опасностей — тот же EMPTY_BLOCKS, поэтому зависимые не пересчитываются зря.
 */
export const hazardBlocks = computed(() => {
  const v = venue.value
  const g = graph.value
  return v && g ? venueHazardBlocks(v, g, activeHazards.value, liveHazardUnits.value) : EMPTY_BLOCKS
})

/** Граф для обычных маршрутов — без перекрытого; без опасностей — тот же объект `graph`. */
export const routingGraph = computed(() => {
  const g = graph.value
  return g ? filterGraph(g, hazardBlocks.value) : null
})

/** Вошедший сотрудник площадки (собственник/админ): доступен режим «Отметить опасность». */
export const staffAccess = signal(false)

/** Режим персонала: следующий тап по карте ставит точку опасности. */
export const hazardPickMode = signal(false)

/** Точка, поставленная тапом в режиме hazardPickMode; null — ещё не ставили. */
export const hazardPickPoint = signal<readonly [number, number] | null>(null)

/** Активный этаж плоского floor-режима (№11); null = все уровни (одноэтажка). */
export const activeLevelId = signal<string | null>(null)

/** Уличное плечо до входа (№23): рисуется отдельным слоем, ETA суммируется. */
export const externalLeg = signal<import('./route-splice').ExternalLeg | null>(null)

/** Профиль подсказок ведения (№5): тихий/вибро/голос; init в main из localStorage. */
export const alertProfile = signal<'silent' | 'vibrate' | 'voice'>('silent')

/** Индексы уже анонсированных шагов текущего ведения (-1 = прибытие). */
export const announcedSteps = new Set<number>()

/** Новый билд активирован под живой вкладкой в «неспокойном» состоянии —
 *  показать промт «Обновить» (спека 01; тихий reload порвал бы ведение). */
export const updateReady = signal(false)

/** Достижима ли выбранная цель безступенчатым (wheelchair) маршрутом. */
export const stepFreeOk = signal<boolean | null>(null)

/** Фактическое время пути после прибытия, сек («дошли за N мин»). */
export const arrivedAfterS = signal<number | null>(null)

/** Фаза ОРИЕНТАЦИЯ (спека 05): мировой азимут взгляда после привязки стрелки. */
export const orientHeading = signal<number | null>(null)
/** Доступ к датчикам ориентации: idle — привязка ещё не запрашивалась. */
export type OrientSensors = 'idle' | 'granted' | 'denied' | 'unsupported'
export const orientSensors = signal<OrientSensors>('idle')
/** Привязка устарела (дрейф гиро) — честный бейдж вместо ложной уверенности. */
export const orientStale = signal(false)

/**
 * AR-ведение (спека 13): способ движения выбирает пользователь — «карта и
 * линия» или «AR-ведение». Выбор живёт на устройстве (ключ guidance).
 */
export type GuidancePref = 'map' | 'ar'

function initialGuidancePref(): GuidancePref {
  try {
    return localStorage.getItem('guidance') === 'ar' ? 'ar' : 'map'
  } catch {
    return 'map'
  }
}
export const guidancePref = signal<GuidancePref>(initialGuidancePref())

export function setGuidancePref(pref: GuidancePref): void {
  guidancePref.value = pref
  try {
    localStorage.setItem('guidance', pref)
  } catch {
    // приватный режим — выбор не сохраняется
  }
}

/** Активная AR-сессия поверх карты: обычное ведение или эвакуация. */
export const arSession = signal<'follow' | 'evac' | null>(null)

/**
 * Детент панели ведения (спека 04): экран телефона маленький, карта важнее
 * панели. hidden — только ручка внизу, mini — одна строка (по умолчанию),
 * full — подробности. Переключается свайпом и тапом.
 */
export type FollowDetent = 'hidden' | 'mini' | 'full'
export const followDetent = signal<FollowDetent>('mini')

/** Текущий режим AR-оверлея (null — оверлея нет): панель ведения прячется
 *  сама, когда камера занимает весь экран, и возвращается при опускании. */
export const arMode = signal<'ar' | 'split' | 'map' | null>(null)

/** Доля экрана под камеру в режиме «камера + карта»; тянется за разделитель. */
export const AR_SPLIT_MIN = 0.35
/** Выше 0.7 карту съедают сегмент-контрол и строка ведения — «карты» не остаётся. */
export const AR_SPLIT_MAX = 0.7
const AR_SPLIT_DEFAULT = 0.56

export function clampSplitRatio(ratio: number): number {
  if (!Number.isFinite(ratio)) return AR_SPLIT_DEFAULT
  return Math.min(AR_SPLIT_MAX, Math.max(AR_SPLIT_MIN, ratio))
}

function initialSplitRatio(): number {
  try {
    const raw = localStorage.getItem('arSplit')
    return raw === null ? AR_SPLIT_DEFAULT : clampSplitRatio(Number.parseFloat(raw))
  } catch {
    return AR_SPLIT_DEFAULT
  }
}
export const arSplitRatio = signal<number>(initialSplitRatio())

/** Живое значение во время перетаскивания (в хранилище не пишем — 60 раз/с). */
export function setSplitRatio(ratio: number): void {
  arSplitRatio.value = clampSplitRatio(ratio)
}

/** Жест закончен — вот теперь сохраняем. */
export function commitSplitRatio(): void {
  try {
    localStorage.setItem('arSplit', arSplitRatio.value.toFixed(3))
  } catch {
    // приватный режим — пропорция живёт до конца сессии
  }
}

/**
 * Что показывать, когда телефон поднят: полный AR или «камера + карта».
 * Отдельной настройки нет — предпочтение запоминается из ручного выбора
 * в сегмент-контроле, и авто-режим дальше уважает его.
 */
export type ArRaisePref = 'ar' | 'split'

function initialRaisePref(): ArRaisePref {
  try {
    return localStorage.getItem('arRaise') === 'split' ? 'split' : 'ar'
  } catch {
    return 'ar'
  }
}
export const arRaisePref = signal<ArRaisePref>(initialRaisePref())

export function setRaisePref(pref: ArRaisePref): void {
  arRaisePref.value = pref
  try {
    localStorage.setItem('arRaise', pref)
  } catch {
    // приватный режим — предпочтение живёт до конца сессии
  }
}

/** Уведомление AR-слоя (i18n-ключ) — живёт в App и переживает закрытие
 *  оверлея: «камера недоступна» обязана быть видна ПОСЛЕ ухода AR. */
export const arNotice = signal<import('@app/i18n').I18nKey | null>(null)

/** «Карта по курсу» (T1): bearing карты следует за азимутом взгляда. */
export const headingUpOn = signal(false)

/** Человек двигает карту руками — камера не следует за позицией и не крутится
 *  по курсу, пока он не вернёт её кнопкой или не оставит карту в покое
 *  (nav/camera-hold.ts). Точка «вы здесь» и маршрут при этом живые. */
export const cameraHeld = signal(false)
/** Время последнего касания карты (мс) — от него отсчитывается возврат. */
export const lastMapGestureMs = signal(0)

/** Привязка компаса из фазы ОРИЕНТАЦИЯ — переносится в AR-ведение как есть,
 *  с возрастом (честный stale вместо ложной уверенности). */
export const orientBinding = signal<import('./nav/orientation').OrientationBinding | null>(null)

/** Браузер оффлайн (H1): бейдж честно говорит «карта из кеша». */
export const isOffline = signal(typeof navigator !== 'undefined' && !navigator.onLine)

/** Оверлей поиска (F1) открыт. */
export const searchOpen = signal(false)

/** Стили подложки (OpenFreeMap): тёмный/чертёжный/классика/яркий/фьорд.
 *  Выбор живёт на устройстве (ключ basemap). */
export const BASEMAP_MODES = ['dark', 'cad', 'liberty', 'bright', 'fiord', 'saved'] as const
export type BasemapChoice = (typeof BASEMAP_MODES)[number]

function initialBasemap(): BasemapChoice {
  // Aurora (дизайн 05.08.2026): светлая «Aurora Paper» — тема по умолчанию,
  // поэтому и подложка по умолчанию светлая; тёмные — осознанный выбор.
  try {
    const saved = localStorage.getItem('basemap')
    return (BASEMAP_MODES as readonly string[]).includes(saved ?? '')
      ? (saved as BasemapChoice)
      : 'liberty'
  } catch {
    return 'liberty'
  }
}
export const basemapMode = signal<BasemapChoice>(initialBasemap())

/** Есть ли сохранённая OPFS-вырезка карты площадки (пункт «Сохранённая»). */
export const savedMapAvailable = signal(false)

/** Объёмный показ зданий (3D-экструзия по этажности), как в редакторе.
 *  Выбор живёт на устройстве (ключ view3d). */
function initialView3D(): boolean {
  try {
    return localStorage.getItem('view3d') === '1'
  } catch {
    return false
  }
}
export const view3D = signal(initialView3D())

/** Подробности карточки цели: посетитель сам решает объём информации
 *  (UX: progressive disclosure). Выбор живёт на устройстве (ключ cardDetails). */
function initialCardDetails(): boolean {
  try {
    return localStorage.getItem('cardDetails') !== 'off'
  } catch {
    return true
  }
}
export const cardDetailsOpen = signal(initialCardDetails())

export function toggleCardDetails(): void {
  cardDetailsOpen.value = !cardDetailsOpen.value
  try {
    localStorage.setItem('cardDetails', cardDetailsOpen.value ? 'on' : 'off')
  } catch {
    // приватный режим — выбор не сохраняется
  }
}

/** Тап по обычному объекту во время ЧС: маршруты заблокированы — тик
 *  показывает подсказку «только к укрытиям/выходам» (анти-путаница). */
export const emergencyRouteLockTick = signal(0)

/** Тик пересборки стиля карты (setStyle): аварийные эффекты перевешиваются. */
export const styleResetTick = signal(0)

/** Легенда территории открыта (кнопка в рельсе инструментов). */
export const legendOpen = signal(false)

/** Каталог объектов (правая панель, hover-подсветка на карте). */
export const catalogOpen = signal(false)

/** Площадка подключила ИИ-ассистента (ADR 0020, /v/{id}/assistant.json).
 *  Признаки ИИ в интерфейсе (пилюля подсказок, карточка ответа в поиске)
 *  показываются ТОЛЬКО при нём: обещать несуществующую помощь нечестно. */
export const assistantOn = signal(false)

/** В браузере есть живая сессия кабинета с правами на эту площадку
 *  (владелец/админ) — только тогда видна ссылка «Редактировать». */
export const canEditVenue = signal(false)

/** «Объём» из контура (фаза P): id здания-этажерки и активный этаж. */
export const floorStackUnitId = signal<string | null>(null)
/** Открытый «Объём 3D» (three.js-сцена здания); null — закрыт. */
export const volume3dUnitId = signal<string | null>(null)
export const floorStackFloor = signal(1)

/** Каталог этажа ТЦ (mall-пресет): открыт ли список магазинов. */
export const mallDirectoryOpen = signal(false)

/** A-to-B планировщик маршрута (Terra): открыт ли. */
export const a2bOpen = signal(false)

/** Замер CAD (Terra): активен ли режим и поставленные точки. */
export const measureActive = signal(false)
export const measurePoints = signal<readonly (readonly [number, number])[]>([])

/** QR-пропуск: id цели, для которой открыт модал (null — закрыт). */
export const qrPassTargetId = signal<string | null>(null)

/** Живой слой карточек по unit_id (ADR 0014); null — сервер молчит, статика. */
export const liveOccupants = signal<ReadonlyMap<string, import('./occupants').LiveOccupant> | null>(
  null,
)

/**
 * Верифицированный аварийный кадр (G4, ADR 0009): null = обычный режим.
 * Устанавливается в main.tsx после успешной verifyEmergencyParam.
 * EMERGENCY — 5-е состояние поверх машины (не заменяет appState).
 */
export const emergencyFrame = signal<import('./emergency/frame').EmergencyFrame | null>(null)

/** Уровень 0 (план 21.09): линия маршрута из напечатанной таблички
 *  (`&r=`, Google polyline точность 5) — рисуется даже без venue.json. */
export const signRoute = signal<readonly (readonly [number, number])[] | null>(null)

/** Чанк движка карты не загрузился (нет сети и нет кеша): режим ЧС
 *  показывает SVG-схему площадки без MapLibre (план 21.09, п.5). */
export const engineFailed = signal(false)
